/** CAM-02: the codes our labels print (Code 128 carries a SKU without a barcode). */
export const SCAN_FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128'] as const;

/** How long a code must be out of view before it counts again (CAM-03). */
export const REPEAT_GAP_MS = 1200;

export interface ReadBarcode {
  format: string;
  rawValue: string;
}

export interface BarcodeReader {
  detect(source: HTMLVideoElement): Promise<ReadBarcode[]>;
}

/**
 * The code as the product holds it. A UPC-A label is an EAN-13 starting with 0: ZXing reads it so,
 * the browser's own reader as its 12 digits, which labels print and products store.
 */
export function scannedCode({ format, rawValue }: ReadBarcode): string {
  const code = rawValue.trim();
  return format === 'ean_13' && /^0\d{12}$/.test(code) ? code.slice(1) : code;
}

interface NativeDetectorClass {
  new (options: { formats: string[] }): BarcodeReader;
  getSupportedFormats(): Promise<string[]>;
}

/** CAM-01: a secure page with camera access (HTTPS, or localhost). */
export const cameraAvailable = () =>
  typeof window !== 'undefined' &&
  window.isSecureContext &&
  typeof navigator.mediaDevices?.getUserMedia === 'function';

/**
 * CAM-03: the codes seen now that were out of view for `gap` ms or more. A code held in front of
 * the camera is read once; taken away and shown again, once more.
 */
export function createRepeatGuard(gap = REPEAT_GAP_MS) {
  const lastSeen = new Map<string, number>();
  return (codes: string[], now: number): string[] => {
    const fresh = [...new Set(codes)].filter((code) => {
      const seen = lastSeen.get(code);
      return seen === undefined || now - seen >= gap;
    });
    for (const code of codes) lastSeen.set(code, now);
    for (const [code, seen] of lastSeen) if (now - seen >= gap) lastSeen.delete(code);
    return fresh;
  };
}

export type CameraProblem = 'denied' | 'noCamera' | 'busy' | 'failed';

/** CAM-07: what went wrong with the camera, from the browser's error. */
export function cameraProblem(error: unknown): CameraProblem {
  const name = error instanceof Error || error instanceof DOMException ? error.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied';
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'noCamera';
  if (name === 'NotReadableError' || name === 'AbortError') return 'busy';
  return 'failed';
}

let reader: Promise<BarcodeReader> | null = null;

/**
 * CAM-05: the browser's own reader when it reads our formats, otherwise ZXing in WebAssembly,
 * loaded on first use from the app's own files.
 */
export function loadBarcodeReader(): Promise<BarcodeReader> {
  reader ??= (async () => {
    const Native = (globalThis as { BarcodeDetector?: NativeDetectorClass }).BarcodeDetector;
    const supported = Native ? await Native.getSupportedFormats().catch(() => []) : [];
    if (Native && supported.includes('ean_13')) {
      return new Native({ formats: SCAN_FORMATS.filter((format) => supported.includes(format)) });
    }
    const [{ BarcodeDetector, prepareZXingModule }, { default: wasmUrl }] = await Promise.all([
      import('barcode-detector/ponyfill'),
      import('zxing-wasm/reader/zxing_reader.wasm?url'),
    ]);
    prepareZXingModule({
      overrides: {
        locateFile: (path: string, prefix: string) =>
          path.endsWith('.wasm') ? wasmUrl : prefix + path,
      },
    });
    return new BarcodeDetector({ formats: [...SCAN_FORMATS] });
  })();
  // A failed load (a lost connection) is tried again next time.
  reader.catch(() => {
    reader = null;
  });
  return reader;
}

let audio: AudioContext | null = null;

/** Call from a tap: iOS plays sound only from a context started by the person. */
export function unlockBeep(): void {
  try {
    audio ??= new AudioContext();
    void audio.resume();
  } catch {
    audio = null;
  }
}

/** A short beep and, where the phone can, a vibration: a code was read. */
export function signalRead(): void {
  navigator.vibrate?.(60);
  if (!audio || audio.state !== 'running') return;
  const tone = audio.createOscillator();
  const volume = audio.createGain();
  tone.frequency.value = 1400;
  volume.gain.value = 0.08;
  tone.connect(volume).connect(audio.destination);
  tone.start();
  tone.stop(audio.currentTime + 0.08);
}
